import React, { useEffect, useMemo, useState } from "react";
import {
  CheckSquare,
  Plus,
  Pencil,
  Trash2,
  Check,
  X,
  Calendar,
  Phone,
  RotateCcw,
  Send,
  Package,
  FileText,
  MapPin,
  CircleDot,
  Sparkles,
  ThumbsUp,
  AlertTriangle,
  User as UserIcon,
  Lock,
} from "lucide-react";
import {
  fetchTasks,
  createTask,
  updateTask,
  deleteTask,
  createInteraction,
  fetchUsers,
} from "../lib/api";
import { useAuth } from "../lib/AuthProvider";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { toast } from "sonner";

const TYPE_OPTIONS = [
  { value: "call_back",       label: "Call back",      Icon: Phone },
  { value: "follow_up",       label: "Follow up",      Icon: RotateCcw },
  { value: "send_sample",     label: "Send sample",    Icon: Package },
  { value: "send_proposal",   label: "Send proposal",  Icon: Send },
  { value: "schedule_visit",  label: "Schedule visit", Icon: MapPin },
  { value: "other",           label: "Other",          Icon: FileText },
];

const TYPE_META = Object.fromEntries(
  TYPE_OPTIONS.map((t) => [t.value, t])
);

const TYPE_COLOR = {
  call_back:      "text-emerald-300 bg-emerald-500/10 border-emerald-500/30",
  follow_up:      "text-sky-300    bg-sky-500/10    border-sky-500/30",
  send_sample:    "text-amber-300  bg-amber-500/10  border-amber-500/30",
  send_proposal:  "text-violet-300 bg-violet-500/10 border-violet-500/30",
  schedule_visit: "text-pink-300   bg-pink-500/10   border-pink-500/30",
  other:          "text-zinc-300   bg-zinc-500/10   border-zinc-500/30",
};

const EMPTY_FORM = {
  title: "",
  description: "",
  task_type: "follow_up",
  due_date: "",
  assigned_to_id: "",
};

// Map task_type → interaction.type for the auto-logged Interaction
// that gets created when a sales rep checks a task off.
const TASK_TO_INTERACTION_TYPE = {
  call_back:      "call",
  follow_up:      "note",
  send_sample:    "visit",
  send_proposal:  "email",
  schedule_visit: "visit",
  other:          "note",
};

// Outcome chip definitions for the completion panel.
const OUTCOMES = [
  { value: "connected",         label: "Went well",        Icon: ThumbsUp,        cls: "bg-emerald-500/10 border-emerald-500/30 text-emerald-200 hover:bg-emerald-500/20" },
  { value: "follow_up_needed",  label: "Needs follow-up",  Icon: AlertTriangle,   cls: "bg-amber-500/10   border-amber-500/30   text-amber-200   hover:bg-amber-500/20" },
  { value: "no_answer",         label: "No answer",        Icon: X,               cls: "bg-zinc-700/40    border-zinc-700        text-zinc-300    hover:bg-zinc-700/60" },
];

/** "Jun 3" / "Today" / "Tomorrow" / "5d overdue" etc. */
function formatDueDate(dueIso) {
  if (!dueIso) return null;
  const due = new Date(dueIso + "T00:00:00");
  if (Number.isNaN(due.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = Math.round((due - today) / 86400000);
  if (diff === 0) return { label: "Today",     tone: "amber" };
  if (diff === 1) return { label: "Tomorrow",  tone: "amber" };
  if (diff < 0)   return { label: `${-diff}d overdue`, tone: "rose" };
  if (diff <= 7)  return {
    label: due.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
    tone: "emerald",
  };
  return {
    label: due.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
    tone: "zinc",
  };
}

const TONE_CLS = {
  rose:    "text-rose-300    bg-rose-500/10    border-rose-500/30",
  amber:   "text-amber-300   bg-amber-500/10   border-amber-500/30",
  emerald: "text-emerald-300 bg-emerald-500/10 border-emerald-500/30",
  zinc:    "text-zinc-400    bg-zinc-500/10    border-zinc-700/40",
};

/**
 * Per-account task checklist. Mounted on the Dispensary Detail page
 * between People (account context) and the Activity Timeline (history).
 * Tasks are forward-looking — what needs to happen next.
 */
export function DispensaryTasks({ dispensaryId, dispensary }) {
  const { isAdmin, isDispatcher, user: currentUser } = useAuth();
  const canAssign = isAdmin || isDispatcher;
  // Sales reps can only edit/delete tasks THEY created. Tasks an admin
  // (or anyone else) delegated to them are locked — they can still
  // mark them done, just can't rename/retype/re-date/delete.
  // Admins + dispatchers can edit anything.
  const canEditTask = (t) => {
    if (isAdmin || isDispatcher) return true;
    if (!t.assigned_by_id) return true;             // legacy / self-created
    return t.assigned_by_id === currentUser?.user_id;
  };
  const [tasks, setTasks] = useState(null);
  const [editing, setEditing] = useState(null); // task.id | "new" | null
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [showDone, setShowDone] = useState(false);
  // Roster of users this dispensary CAN be assigned to (matches the
  // contact id on `dispensary.assigned_to_id`). Loaded lazily.
  const [eligibleReps, setEligibleReps] = useState([]);

  // Task id currently being marked done — surfaces the inline
  // "log what happened" panel below that row.
  const [completing, setCompleting] = useState(null);
  const [completionNotes, setCompletionNotes] = useState("");
  const [completionOutcome, setCompletionOutcome] = useState(null);
  const [completionSaving, setCompletionSaving] = useState(false);

  const load = async () => {
    if (!dispensaryId) return;
    try {
      const list = await fetchTasks(dispensaryId);
      setTasks(list);
    } catch {
      setTasks([]);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispensaryId]);

  // Admins/dispatchers — pull the user roster so the assign dropdown
  // can list real reps. Only the owning rep of this dispensary is
  // assignable per backend validation.
  useEffect(() => {
    if (!canAssign) return;
    fetchUsers()
      .then((users) => setEligibleReps(users || []))
      .catch(() => setEligibleReps([]));
  }, [canAssign]);

  // Map user_id → user object so rows can display the assignee name.
  const userById = useMemo(() => {
    const m = {};
    for (const u of eligibleReps) m[u.user_id] = u;
    if (currentUser && !m[currentUser.user_id]) m[currentUser.user_id] = currentUser;
    return m;
  }, [eligibleReps, currentUser]);

  const startNew = () => {
    setForm(EMPTY_FORM);
    setEditing("new");
  };

  const startEdit = (t) => {
    setForm({
      title: t.title || "",
      description: t.description || "",
      task_type: t.task_type || "follow_up",
      due_date: t.due_date || "",
      assigned_to_id: t.assigned_to_id || "",
    });
    setEditing(t.id);
  };

  const cancel = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
  };

  const save = async () => {
    const title = form.title.trim();
    if (!title) {
      toast.error("Task title is required");
      return;
    }
    try {
      setSaving(true);
      const payload = {
        title,
        description: form.description.trim(),
        task_type: form.task_type,
        due_date: form.due_date || null,
      };
      // Only admins/dispatchers send assigned_to_id — sales reps' tasks
      // auto-default to themselves on the backend.
      if (canAssign) {
        payload.assigned_to_id = form.assigned_to_id || null;
      }
      if (editing === "new") {
        await createTask(dispensaryId, payload);
        toast.success("Task added");
      } else {
        await updateTask(editing, payload);
        toast.success("Task updated");
      }
      cancel();
      load();
    } catch (e) {
      toast.error("Could not save task", { description: String(e?.response?.data?.detail || e) });
    } finally {
      setSaving(false);
    }
  };

  const toggleDone = async (t) => {
    // Re-opening a done task is one click, no prompt.
    if (t.status === "done") {
      try {
        await updateTask(t.id, { status: "todo" });
        load();
      } catch {
        toast.error("Could not update task");
      }
      return;
    }
    // Marking a todo as done — surface the "log what happened" panel
    // instead of completing immediately. The user can still skip the
    // logging via "Just mark done" inside the panel.
    setCompleting(t.id);
    setCompletionNotes("");
    setCompletionOutcome(null);
  };

  const cancelCompletion = () => {
    setCompleting(null);
    setCompletionNotes("");
    setCompletionOutcome(null);
    setCompletionSaving(false);
  };

  const finishCompletion = async (task, { logInteraction }) => {
    try {
      setCompletionSaving(true);
      // 1. Always mark the task done.
      await updateTask(task.id, { status: "done" });
      // 2. Optionally log an Interaction so the timeline gets the context.
      if (logInteraction) {
        const interactionType =
          TASK_TO_INTERACTION_TYPE[task.task_type] || "note";
        const headline = `Completed task: ${task.title}`;
        const body = completionNotes.trim();
        const combined = body ? `${headline}\n\n${body}` : headline;
        await createInteraction(task.dispensary_id, {
          type: interactionType,
          outcome: completionOutcome || undefined,
          notes: combined,
        });
        toast.success("Task done · activity logged");
      } else {
        toast.success("Task done");
      }
      cancelCompletion();
      load();
      // Refresh the activity timeline below us if it's listening.
      window.dispatchEvent(new Event("jacktrack:interactions-changed"));
    } catch (e) {
      toast.error("Could not complete task", { description: String(e) });
      setCompletionSaving(false);
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete this task?\n\n"${t.title}"`)) return;
    try {
      await deleteTask(t.id);
      toast.success("Task deleted");
      load();
    } catch {
      toast.error("Could not delete task");
    }
  };

  const open = (tasks || []).filter((t) => t.status === "todo");
  const done = (tasks || []).filter((t) => t.status === "done");

  return (
    <div className="panel p-5" data-testid="dispensary-tasks-section">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <CheckSquare className="w-4 h-4 text-emerald-400" />
          <h3 className="text-sm font-semibold tracking-tight text-zinc-100">
            Tasks
          </h3>
          {open.length > 0 && (
            <span
              data-testid="task-open-count"
              className="text-[10px] uppercase tracking-wider font-mono px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
            >
              {open.length} open
            </span>
          )}
          {done.length > 0 && (
            <button
              data-testid="task-toggle-done"
              type="button"
              onClick={() => setShowDone((v) => !v)}
              className="text-[10px] uppercase tracking-wider font-mono text-zinc-500 hover:text-zinc-300"
            >
              {showDone ? "Hide" : "Show"} {done.length} done
            </button>
          )}
        </div>
        {editing !== "new" && (
          <Button
            data-testid="task-add-btn"
            size="sm"
            onClick={startNew}
            className="bg-emerald-600 hover:bg-emerald-500 text-emerald-50 h-8"
          >
            <Plus className="w-3.5 h-3.5 mr-1.5" />
            Add task
          </Button>
        )}
      </div>

      {tasks === null ? (
        <div className="text-xs text-zinc-500">Loading…</div>
      ) : (
        <>
          {/* Inline new-task form */}
          {editing === "new" && (
            <TaskForm
              form={form}
              setForm={setForm}
              onSave={save}
              onCancel={cancel}
              saving={saving}
              isNew
              canAssign={canAssign}
              eligibleReps={eligibleReps}
              dispensary={dispensary}
            />
          )}

          {/* Open tasks */}
          {open.length === 0 && editing !== "new" && (
            <div className="text-xs text-zinc-500 italic py-3" data-testid="task-empty-state">
              No open tasks. Click <strong className="text-zinc-300">Add task</strong> to set the next thing
              that needs to happen on this account.
            </div>
          )}

          {open.length > 0 && (
            <ul className="space-y-2">
              {open.map((t) =>
                editing === t.id ? (
                  <li key={t.id}>
                    <TaskForm
                      form={form}
                      setForm={setForm}
                      onSave={save}
                      onCancel={cancel}
                      saving={saving}
                      canAssign={canAssign}
                      eligibleReps={eligibleReps}
                      dispensary={dispensary}
                    />
                  </li>
                ) : (
                  <React.Fragment key={t.id}>
                    <TaskRow
                      task={t}
                      assignee={t.assigned_to_id ? userById[t.assigned_to_id] : null}
                      onToggleDone={() => toggleDone(t)}
                      onEdit={() => startEdit(t)}
                      onDelete={() => remove(t)}
                      busy={completing === t.id}
                      canEdit={canEditTask(t)}
                    />
                    {completing === t.id && (
                      <li>
                        <CompletionPanel
                          task={t}
                          notes={completionNotes}
                          setNotes={setCompletionNotes}
                          outcome={completionOutcome}
                          setOutcome={setCompletionOutcome}
                          saving={completionSaving}
                          onCancel={cancelCompletion}
                          onSave={(opts) => finishCompletion(t, opts)}
                        />
                      </li>
                    )}
                  </React.Fragment>
                )
              )}
            </ul>
          )}

          {/* Done tasks — hidden by default */}
          {showDone && done.length > 0 && (
            <ul className="mt-4 pt-4 border-t border-zinc-800 space-y-2 opacity-80">
              {done.map((t) => (
                <TaskRow
                  key={t.id}
                  task={t}
                  assignee={t.assigned_to_id ? userById[t.assigned_to_id] : null}
                  onToggleDone={() => toggleDone(t)}
                  onEdit={() => startEdit(t)}
                  onDelete={() => remove(t)}
                  canEdit={canEditTask(t)}
                />
              ))}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

function TaskRow({ task, assignee, onToggleDone, onEdit, onDelete, busy = false, canEdit = true }) {
  const meta = TYPE_META[task.task_type] || TYPE_META.other;
  const TypeIcon = meta.Icon;
  const due = formatDueDate(task.due_date);
  const done = task.status === "done";
  const assigneeLabel = assignee
    ? (assignee.name || assignee.email || "Rep")
    : null;
  return (
    <li
      data-testid={`task-row-${task.id}`}
      className={`group flex items-start gap-3 rounded-md border bg-zinc-950/40 px-3 py-2.5 transition-colors ${
        busy
          ? "border-emerald-500/40 ring-1 ring-emerald-500/20"
          : "border-zinc-800 hover:border-zinc-700"
      }`}
    >
      <button
        type="button"
        data-testid={`task-checkbox-${task.id}`}
        onClick={onToggleDone}
        className={`shrink-0 mt-0.5 w-4 h-4 rounded border flex items-center justify-center transition-colors ${
          done
            ? "bg-emerald-500 border-emerald-500 text-emerald-50"
            : "border-zinc-700 hover:border-emerald-500 text-transparent hover:text-emerald-400"
        }`}
        aria-label={done ? "Mark as not done" : "Mark as done"}
      >
        <Check className="w-3 h-3" />
      </button>

      <div className="flex-1 min-w-0 space-y-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className={`text-sm leading-snug ${
              done ? "text-zinc-500 line-through" : "text-zinc-100"
            }`}
          >
            {task.title}
          </span>
          <span
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono uppercase tracking-wider border ${
              TYPE_COLOR[task.task_type] || TYPE_COLOR.other
            }`}
          >
            <TypeIcon className="w-2.5 h-2.5" />
            {meta.label}
          </span>
          {due && !done && (
            <span
              className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono uppercase tracking-wider border ${TONE_CLS[due.tone]}`}
            >
              <Calendar className="w-2.5 h-2.5" />
              {due.label}
            </span>
          )}
          {assigneeLabel && (
            <span
              data-testid={`task-assignee-${task.id}`}
              title={`Assigned to ${assigneeLabel}`}
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-mono uppercase tracking-wider border border-indigo-500/30 bg-indigo-500/10 text-indigo-300"
            >
              <UserIcon className="w-2.5 h-2.5" />
              {assigneeLabel}
            </span>
          )}
        </div>
        {task.description && !done && (
          <p className="text-xs text-zinc-500 leading-relaxed whitespace-pre-wrap">
            {task.description}
          </p>
        )}
      </div>

      <div className="shrink-0 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
        {canEdit ? (
          <>
            <button
              type="button"
              data-testid={`task-edit-${task.id}`}
              onClick={onEdit}
              className="p-1.5 text-zinc-500 hover:text-zinc-200 rounded"
              aria-label="Edit task"
            >
              <Pencil className="w-3.5 h-3.5" />
            </button>
            <button
              type="button"
              data-testid={`task-delete-${task.id}`}
              onClick={onDelete}
              className="p-1.5 text-zinc-500 hover:text-rose-300 rounded"
              aria-label="Delete task"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </>
        ) : (
          <span
            data-testid={`task-locked-${task.id}`}
            title="Admin-assigned — only the admin who created this task can edit it"
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono uppercase tracking-wider border border-amber-500/30 bg-amber-500/10 text-amber-300"
          >
            <Lock className="w-2.5 h-2.5" />
            Locked
          </span>
        )}
      </div>
    </li>
  );
}

function TaskForm({ form, setForm, onSave, onCancel, saving, isNew = false, canAssign = false, eligibleReps = [], dispensary = null }) {
  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));
  // Filter the roster to reps who can OWN this dispensary. Sales reps
  // own dispensaries by Contact.id stored on `dispensary.assigned_to_id`.
  // For now we just present all sales/admin users — backend validates.
  const repOptions = eligibleReps.filter((u) =>
    ["sales", "admin", "dispatcher"].includes(u.role)
  );
  return (
    <div
      data-testid={isNew ? "task-form-new" : "task-form-edit"}
      className="rounded-md border border-emerald-500/30 bg-zinc-950 p-3 mb-3 space-y-2.5"
    >
      <Input
        autoFocus
        data-testid="task-title-input"
        value={form.title}
        onChange={set("title")}
        placeholder="What needs to happen?"
        className="bg-zinc-900 border-zinc-800 text-zinc-100 h-9"
        onKeyDown={(e) => {
          if (e.key === "Enter" && !e.shiftKey && form.title.trim()) {
            e.preventDefault();
            onSave();
          }
          if (e.key === "Escape") onCancel();
        }}
      />
      <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-2">
        <Select
          value={form.task_type}
          onValueChange={(v) => setForm((f) => ({ ...f, task_type: v }))}
        >
          <SelectTrigger
            data-testid="task-type-select"
            className="bg-zinc-900 border-zinc-800 text-zinc-100 h-9"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
            {TYPE_OPTIONS.map((o) => {
              const Icon = o.Icon;
              return (
                <SelectItem key={o.value} value={o.value}>
                  <span className="inline-flex items-center gap-2">
                    <Icon className="w-3.5 h-3.5" />
                    {o.label}
                  </span>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
        <Input
          data-testid="task-due-date-input"
          type="date"
          value={form.due_date}
          onChange={set("due_date")}
          className="bg-zinc-900 border-zinc-800 text-zinc-100 h-9 font-mono w-full sm:w-44"
        />
      </div>
      {canAssign && (
        <div className="space-y-1">
          <label className="text-[10px] uppercase tracking-wider font-mono text-zinc-500">
            Assign to
          </label>
          <Select
            value={form.assigned_to_id || "__unassigned__"}
            onValueChange={(v) =>
              setForm((f) => ({
                ...f,
                assigned_to_id: v === "__unassigned__" ? "" : v,
              }))
            }
          >
            <SelectTrigger
              data-testid="task-assignee-select"
              className="bg-zinc-900 border-zinc-800 text-zinc-100 h-9"
            >
              <SelectValue placeholder="Unassigned" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
              <SelectItem value="__unassigned__">
                <span className="text-zinc-400">Unassigned</span>
              </SelectItem>
              {repOptions.map((u) => (
                <SelectItem key={u.user_id} value={u.user_id}>
                  <span className="inline-flex items-center gap-2">
                    {u.name || u.email}
                    <span className="text-[10px] uppercase font-mono text-zinc-500">
                      {u.role}
                    </span>
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {dispensary?.assigned_to_id ? null : (
            <p className="text-[10px] text-amber-400/80">
              Heads up — this dispensary has no owning rep yet. Only the
              dispensary's owner can be assigned tasks here.
            </p>
          )}
        </div>
      )}
      <Textarea
        data-testid="task-description-input"
        value={form.description}
        onChange={set("description")}
        rows={2}
        placeholder="Optional context — what to discuss, what samples, etc."
        className="bg-zinc-900 border-zinc-800 text-zinc-100 text-xs resize-none"
      />
      <div className="flex items-center justify-end gap-2 pt-1">
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={onCancel}
          className="border-zinc-800 bg-zinc-900 hover:bg-zinc-800 text-zinc-100 h-8"
        >
          <X className="w-3.5 h-3.5 mr-1" />
          Cancel
        </Button>
        <Button
          type="button"
          data-testid="task-save-btn"
          size="sm"
          onClick={onSave}
          disabled={saving || !form.title.trim()}
          className="bg-emerald-600 hover:bg-emerald-500 text-emerald-50 h-8"
        >
          <CircleDot className="w-3.5 h-3.5 mr-1" />
          {isNew ? "Add task" : "Save"}
        </Button>
      </div>
    </div>
  );
}

/**
 * Inline panel that pops up below a task row when its checkbox is
 * clicked. Lets the rep optionally jot what happened + tag an outcome
 * before the task closes, then auto-writes an Interaction onto the
 * dispensary's activity timeline so it shows up in reports + history.
 *
 * Two save paths:
 *   - "Save · log activity"  → close task + create Interaction (preferred)
 *   - "Just mark done"        → close task only (no timeline entry)
 */
function CompletionPanel({
  task,
  notes,
  setNotes,
  outcome,
  setOutcome,
  saving,
  onCancel,
  onSave,
}) {
  return (
    <div
      data-testid="task-completion-panel"
      className="rounded-md border border-emerald-500/30 bg-emerald-500/5 px-4 py-3 mt-1 mb-2 space-y-3"
    >
      <div className="flex items-start gap-2">
        <Sparkles className="w-4 h-4 text-emerald-300 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-zinc-100">
            Nice — anything to note about this?
          </div>
          <div className="text-[11px] text-zinc-500 mt-0.5 leading-relaxed">
            What you type here lands in the Activity Timeline below + on
            Lead Generator reports so the rest of the team can see the
            update. Optional.
          </div>
        </div>
      </div>

      <Textarea
        data-testid="task-completion-notes"
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        rows={2}
        autoFocus
        placeholder={
          task.task_type === "call_back"
            ? "Spoke with Maya — she's reviewing the sample. Wants to chat again next Wed."
            : task.task_type === "send_sample"
            ? "Dropped 3 SKUs with Tom. He'll get them on the menu for the weekend."
            : "What happened?"
        }
        className="bg-zinc-950 border-zinc-800 text-zinc-100 text-xs resize-none focus-visible:ring-emerald-500/30"
      />

      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-[10px] uppercase tracking-wider font-mono text-zinc-500 mr-1">
          Outcome:
        </span>
        {OUTCOMES.map((o) => {
          const Icon = o.Icon;
          const active = outcome === o.value;
          return (
            <button
              key={o.value}
              type="button"
              data-testid={`task-completion-outcome-${o.value}`}
              onClick={() => setOutcome(active ? null : o.value)}
              className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-[11px] font-medium border transition-colors ${
                active
                  ? o.cls + " ring-1 ring-current"
                  : "border-zinc-800 bg-zinc-900/40 text-zinc-400 hover:bg-zinc-800"
              }`}
            >
              <Icon className="w-3 h-3" />
              {o.label}
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center justify-end gap-2 pt-1">
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={onCancel}
          disabled={saving}
          className="text-zinc-400 hover:text-zinc-200 hover:bg-zinc-800 h-8"
        >
          Cancel
        </Button>
        <Button
          type="button"
          data-testid="task-completion-skip"
          size="sm"
          variant="outline"
          onClick={() => onSave({ logInteraction: false })}
          disabled={saving}
          className="border-zinc-800 bg-zinc-900 hover:bg-zinc-800 text-zinc-200 h-8"
        >
          Just mark done
        </Button>
        <Button
          type="button"
          data-testid="task-completion-save"
          size="sm"
          onClick={() => onSave({ logInteraction: true })}
          disabled={saving}
          className="bg-emerald-600 hover:bg-emerald-500 text-emerald-50 h-8"
        >
          <Check className="w-3.5 h-3.5 mr-1" />
          {saving ? "Saving…" : "Save · log activity"}
        </Button>
      </div>
    </div>
  );
}

export default DispensaryTasks;
